import * as ng from 'angular';
import * as uib from 'angular-ui-bootstrap';
import * as _ from 'underscore';

import { EventsApi, EventObjectApi, NamedEventObject, PrizesApiFactory, PrizesApiService } from '..';
import { Prize } from '../shared/prize.model';

/**
 * Find greatest common denominator
 * @param a
 * @param b
 */
function gcd(a:number, b:number) {
   // Since there is a limited precision we need to limit the value.
   if (b < 0.0000001) return a;

   // Discard any fractions due to limitations in precision.
   return gcd(b, Math.floor(a % b));
}

export class PrizesListController {
   static $inject = ['prizesApiFactory', '$uibModal', 'dialogs', 'toast', 'eventsApi'];
   constructor(
      private prizesApiFactory:PrizesApiFactory,
      private $modal:uib.IModalService,
      private dialogs,
      private toast,
      private eventsApi:EventsApi,
   ) { }

   eventId:number;
   prizeAllocationType: string;
   isPrizeStationRequired = false;
   prizesApi:PrizesApiService;
   prizes:Prize[];
   loading = false;

   stations:NamedEventObject[];
   private stationsApi:EventObjectApi;

   simulation:{name:string,count:number}[];

   $onChanges(changesObj:ng.IOnChangesObject) {
      if (changesObj.eventId && changesObj.eventId.currentValue) {
         this.stationsApi = this.eventsApi.getEventObjectApi('prizeStations', this.eventId);
         this.prizesApi = this.prizesApiFactory(this.eventId);
         this.reload();
      }

      if (changesObj.prizeAllocationType && changesObj.prizeAllocationType.currentValue) {
         this.isPrizeStationRequired = this.prizeAllocationType.includes('station');
      }
   }

   getOdds(p:Prize) {
      let quantityTotal = _.reduce(this.prizes, (t, p) => t + p.quantity, 0);
      let divisor = gcd(p.quantity, quantityTotal);

      return `${p.quantity / divisor} in ${quantityTotal / divisor}`;
   }

   async reload() {
      this.loading = true;

      let stations = await this.stationsApi.list();
      return this.prizesApi.list()
         .then((prizes) => {
            this.prizes = prizes
            this.prizes.forEach(p => {
               p.station = stations.find(s => s.id == p.stationId)
            })
         })
         .finally(() => this.loading = false);
   }

   private errorOnSave(error) {
      this.loading = false;
      this.toast.error()
         .message('There was a problem saving the prize, please contact support.')
         .send();
   }

   add(dup:Prize = null) {
      let newPrize:any = {};
      if (dup) {
         for (let key of _.keys(dup)) {
            if (!_.contains(['id', 'slug', 'winCount', 'disabled'], key)) {
               newPrize[key] = dup[key];
            }
         }
      }

      this.$modal.open({
         component: 'prizeEditor',
         resolve: { prize: () => { return newPrize; }, 
            eventId: () => this.eventId, 
            isPrizeStationRequired: () => this.isPrizeStationRequired }
      }).result.then((prize) => {
         this.loading = true;
         this.prizesApi.create(prize)
            .then(() => this.reload())
            .catch(error => this.errorOnSave(error));
      });
   }

   edit(prize:Prize) {
      this.$modal.open({
         component: 'prizeEditor',
         resolve: { prize: () => prize, 
            eventId: () => this.eventId,
            isPrizeStationRequired: () => this.isPrizeStationRequired }
      }).result.then((updated) => {
         this.loading = true;
         this.prizesApi.update(updated)
            .then(() => this.reload())
            .catch(error => this.errorOnSave(error));
      });
   }

   remove(prize:Prize) {
      let cantDeleteMessage = 'This prize has winners associated with it and can not be deleted.';
      if (prize.winCount > 0) {
         this.dialogs.alert(cantDeleteMessage);
         return;
      }

      this.dialogs.confirm(`Are you sure you want to delete "${prize.name}"`).then(() => {
         this.loading = true;
         this.prizesApi.remove(prize)
            .then(() => this.reload())
            .finally(() => this.loading = false)
            .catch(e => {
               if (e.status == 400) {
                  this.dialogs.alert(cantDeleteMessage);
               }
            });
      });
   }

   simulate(iterations) {
      this.prizesApi.simulate(iterations).then((results) => this.simulation = results);
   }
}

ng.module('e360').component('prizesList', {
   templateUrl: require('./prizes-list.html'),
   controller: PrizesListController,
   bindings: {
      eventId: '<',
      prizeAllocationType: '<'
   }
});
